import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { MarketingRouterRule as ContractMarketingRouterRule } from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import {
  type MarketingRouterCreatePayload,
  type MarketingRouterInternalQuery,
  type MarketingRouterUpdatePayload,
  createMarketingRouter,
  listMarketingRouterInternal,
  updateMarketingRouter,
} from '~/services/PromoService';

import type { MarketingRouterRule, MarketingRouterRuleSignificanceMetric } from '~/types/marketingRouterRule';

import { isoToSydneyDate } from '~/utils/dateFormatter';

export type MarketingRouterRuleRecord = MarketingRouterRule & {
  marketingRouterId: string;
  brand: string;
  region: string | null;
  routerStartDate: string | null;
  routerEndDate: string | null;
  ruleJson: Array<MarketingRouterRule>;
};

export const marketingRouterQueryKeys = {
  all: ['marketing-router-rules'] as const,
  lists: () => [...marketingRouterQueryKeys.all, 'list'] as const,
  list: (query: MarketingRouterInternalQuery) => [...marketingRouterQueryKeys.lists(), query] as const,
};

function isSignificanceMetric(value: string): value is MarketingRouterRuleSignificanceMetric {
  return value === 'ttv' || value === 'margin' || value === 'conversion';
}

function normalizeMarketingRouterRule(rule: ContractMarketingRouterRule): MarketingRouterRule {
  return {
    id: rule.id,
    name: rule.name,
    significance_metrics: rule.significance_metrics.filter(isSignificanceMetric),
    start_date: isoToSydneyDate(rule.start_date),
    end_date: isoToSydneyDate(rule.end_date),
    match: {
      ...rule.match,
      utm_source: rule.match.utm_source ?? '',
      utm_medium: rule.match.utm_medium ?? '',
      utm_campaign: rule.match.utm_campaign ?? '',
    },
    variants: rule.variants,
  };
}

function flattenMarketingRouterRules(response: Awaited<ReturnType<typeof listMarketingRouterInternal>>) {
  return response.result.marketingRouters.flatMap((router) =>
    router.rule_json.map((contractRule): MarketingRouterRuleRecord => {
      const rule = normalizeMarketingRouterRule(contractRule);

      return {
        ...rule,
        marketingRouterId: router.marketing_router_id,
        brand: router.brand,
        region: router.region ?? null,
        routerStartDate: router.start_date ?? null,
        routerEndDate: router.end_date ?? null,
        ruleJson: router.rule_json.map(normalizeMarketingRouterRule),
      };
    }),
  );
}

export function useMarketingRouterRules({
  query,
  enabled = true,
}: {
  query: MarketingRouterInternalQuery;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: marketingRouterQueryKeys.list(query),
    queryFn: async ({ signal }) => flattenMarketingRouterRules(await listMarketingRouterInternal(query, { signal })),
    enabled,
  });
}

export function useCreateMarketingRouter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: MarketingRouterCreatePayload) => createMarketingRouter(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingRouterQueryKeys.lists() }),
  });
}

export function useUpdateMarketingRouter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: MarketingRouterUpdatePayload) => updateMarketingRouter(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingRouterQueryKeys.lists() }),
  });
}
